import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  AUTH_TOKEN_KEY,
  agencyOwnerNeedsEmailVerification,
  getApiUrl,
  getDashboardForUser,
  persistAuthSession,
  AGENCY_DASHBOARD_PATH,
  isAgencyOwnerUser,
  sanitizeAuthRedirectUrl,
  getPostSignupRedirectPath,
  FUNDGENIUS_APP_CLIENTS_PATH,
} from '../../utils/authUtils';
import {
  AGENCY_APP_URL,
  getHostnamePortal,
  HOSTNAME_PORTALS,
  isNonProductionAppHost,
} from '../../utils/hostnamePortal';
import { useAuth } from '../../context/AuthContext';

const API = getApiUrl();

const buildVerifyState = (locationState, searchParams) => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const defaultRedirect =
    getHostnamePortal(hostname) === HOSTNAME_PORTALS.FUNDGENIUS_APP
      ? FUNDGENIUS_APP_CLIENTS_PATH
      : AGENCY_DASHBOARD_PATH;
  const rawRedirect = locationState?.redirectTo || searchParams.get('redirect') || defaultRedirect;
  return {
    email: locationState?.email || searchParams.get('email') || '',
    role: locationState?.role || searchParams.get('role') || 'agency_owner',
    redirectTo: sanitizeAuthRedirectUrl(rawRedirect, { hostname }) || defaultRedirect,
    agencyId: locationState?.agencyId || searchParams.get('agency_id') || '',
  };
};

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, checkAuth, updateUser } = useAuth();
  const verifyState = useMemo(
    () => buildVerifyState(location.state, searchParams),
    [location.state, searchParams]
  );

  const [email, setEmail] = useState(verifyState.email);
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const displayEmail = user?.email || email || verifyState.email;
  const hasAuthToken = Boolean(localStorage.getItem(AUTH_TOKEN_KEY));
  const canUseVerificationApi = Boolean(user || hasAuthToken);

  const isAgencyVerificationFlow = useMemo(() => {
    const role = String(verifyState.role || searchParams.get('role') || '').toLowerCase();
    return (
      role === 'agency_owner' ||
      role === 'owner' ||
      Boolean(verifyState.agencyId) ||
      Boolean(searchParams.get('agency_id'))
    );
  }, [verifyState.role, verifyState.agencyId, searchParams]);

  useEffect(() => {
    if (getHostnamePortal() !== HOSTNAME_PORTALS.ADMIN || !isAgencyVerificationFlow) return;
    // Preview / non-production: never bounce verify-email to production app.fundelite.ai.
    if (isNonProductionAppHost()) return;
    const target = new URL('/verify-email', AGENCY_APP_URL);
    searchParams.forEach((value, key) => {
      target.searchParams.set(key, value);
    });
    if (verifyState.email && !target.searchParams.has('email')) {
      target.searchParams.set('email', verifyState.email);
    }
    if (window.location.href !== target.href) {
      window.location.replace(target.href);
    }
  }, [isAgencyVerificationFlow, searchParams, verifyState.email]);

  const resolveRedirectTarget = useCallback(
    (verifiedUser, responseData) => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      const backendRedirect = sanitizeAuthRedirectUrl(responseData?.redirect_to || responseData?.redirect_url, {
        hostname,
      });
      if (backendRedirect) return backendRedirect;
      const stateRedirect = sanitizeAuthRedirectUrl(verifyState.redirectTo, { hostname });
      if (stateRedirect) return stateRedirect;
      if (getHostnamePortal(hostname) === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
        return getPostSignupRedirectPath({ user: verifiedUser, hostname });
      }
      return getDashboardForUser(verifiedUser);
    },
    [verifyState.redirectTo]
  );

  const completeVerification = useCallback(
    async (responseData) => {
      const nextUser = responseData?.user || user;
      const nextToken = responseData?.token || responseData?.access_token;

      if (nextToken || nextUser) {
        persistAuthSession({ token: nextToken, user: nextUser });
        if (nextUser) updateUser({ ...nextUser, email_verified: true, requires_email_verification: false });
        await checkAuth();
      }

      const destination = resolveRedirectTarget(
        { ...(nextUser || user), email_verified: true, requires_email_verification: false },
        responseData
      );
      const path = destination.startsWith('/') ? destination : `/${destination}`;
      const portal = getHostnamePortal();
      if (portal === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
        navigate(path, { replace: true });
        return;
      }
      if (portal === HOSTNAME_PORTALS.ADMIN && isAgencyOwnerUser(nextUser || user)) {
        if (isNonProductionAppHost()) {
          navigate(path, { replace: true });
          return;
        }
        window.location.replace(`${AGENCY_APP_URL}${path}`);
        return;
      }
      navigate(path, { replace: true });
    },
    [checkAuth, navigate, resolveRedirectTarget, updateUser, user]
  );

  const fetchStatus = useCallback(async () => {
    if (!canUseVerificationApi) return;
    try {
      const response = await axios.get(`${API}/auth/email-verification/status`, {
        withCredentials: true,
      });
      setStatus(response.data?.status || 'pending');
      if (response.data?.status === 'verified') {
        toast.success('Email verified!');
        await completeVerification(response.data);
      }
    } catch (error) {
      if (error.response?.status === 401 && !hasAuthToken) {
        setStatus('pending');
      }
    }
  }, [canUseVerificationApi, completeVerification, hasAuthToken]);

  useEffect(() => {
    if (authLoading) return;

    if (user && !agencyOwnerNeedsEmailVerification(user)) {
      navigate(getDashboardForUser(user, verifyState.redirectTo), { replace: true });
      return;
    }

    if (canUseVerificationApi) {
      fetchStatus();
    }
  }, [authLoading, user, navigate, fetchStatus, canUseVerificationApi, verifyState.redirectTo]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    if (!canUseVerificationApi) {
      toast.error('Sign in with your agency account to verify this email.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/email-verification/verify`,
        { code: code.trim(), purpose: 'agency_email_verification' },
        { withCredentials: true }
      );
      toast.success('Email verified! Redirecting to your dashboard.');
      await completeVerification(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed. Try again or request a new code.');
      await fetchStatus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canUseVerificationApi) {
      toast.error('Sign in with your agency account to resend your verification code.');
      return;
    }

    setResendLoading(true);
    try {
      await axios.post(
        `${API}/auth/email-verification/resend`,
        { purpose: 'agency_email_verification' },
        { withCredentials: true }
      );
      toast.success('A new verification code was sent.');
      setCooldown(60);
      await fetchStatus();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 429 && detail.includes('wait')) {
        const match = detail.match(/(\d+)/);
        if (match) setCooldown(parseInt(match[1], 10));
      }
      toast.error(detail || 'Could not resend code. Please try again shortly.');
    } finally {
      setResendLoading(false);
    }
  };

  const statusMessage = {
    locked: 'Too many attempts. Please wait before trying again.',
    expired: 'Your code expired. Request a new code below.',
    pending: 'Enter the 6-digit code we sent to your email.',
    verified: 'Your email is verified.',
    not_required: 'Email verification is not required for your account.',
  }[status] || 'Enter the 6-digit code we sent to your email.';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Verify your agency email</h1>
          <p className="text-slate-600 mt-2">{statusMessage}</p>
          {displayEmail && (
            <p className="text-sm text-slate-500 mt-1 flex items-center justify-center gap-1">
              <Mail className="h-4 w-4" />
              {displayEmail}
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {!canUseVerificationApi && (
            <div className="mb-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Agency owner email</Label>
                <IconInput
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="owner@agency.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={Mail}
                />
              </div>
              <p className="text-sm text-slate-500">
                Sign in with the email you used during agency signup, then return here to enter your verification code.
              </p>
              <Link
                to="/login"
                state={{ from: `/verify-email${displayEmail ? `?email=${encodeURIComponent(displayEmail)}` : ''}` }}
              >
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">Sign in to verify</Button>
              </Link>
            </div>
          )}

          {status === 'not_required' ? (
            <Link to={verifyState.redirectTo || AGENCY_DASHBOARD_PATH}>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700">Continue to dashboard</Button>
            </Link>
          ) : (
            <form onSubmit={handleVerify} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="code">Verification code</Label>
                <IconInput
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  icon={Mail}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading || status === 'locked' || !canUseVerificationApi}
              >
                {loading ? 'Verifying...' : 'Verify email'}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleResend}
                disabled={resendLoading || cooldown > 0 || status === 'locked' || !canUseVerificationApi}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${resendLoading ? 'animate-spin' : ''}`} />
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-emerald-600 hover:text-emerald-700">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
